import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Bot, User, Loader2 } from 'lucide-react';
import yojnasData from '../data/yojnas.json';
import chatbotSpec from '../data/chatbotSpec.json';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (isOpen) {
        setMessages([{ 
            sender: 'bot', 
            text: chatbotSpec.role.hindi 
        }]);
    }
  }, [isOpen]);

  const findScheme = (query) => {
    const lowerQuery = query.toLowerCase();
    const searchFields = yojnasData.searchLogic.fields;
    
    // Prioritize exact matches in nameHi and nameEn
    for (const scheme of yojnasData.schemes) {
        if (scheme.nameHi.toLowerCase() === lowerQuery || scheme.nameEn.toLowerCase() === lowerQuery) {
            return scheme;
        }
    }
    
    // Search in all specified fields from yojnas.json
    return yojnasData.schemes.find(scheme => 
        searchFields.some(field => 
            scheme[field] && scheme[field].toString().toLowerCase().includes(lowerQuery)
        )
    );
  }

  const formatResponse = (scheme) => {
    if (!scheme) {
      return chatbotSpec.fallbackResponse.hindi;
    }

    // Using the structure from chatbotSpec.json
    let response = `**${scheme.nameHi} (${scheme.nameEn})**\n\n`;
    response += `**उद्देश्य:** ${scheme.use}\n\n`;
    if (scheme.documents && scheme.documents.length > 0) {
        response += `**आवश्यक दस्तावेज़:**\n* ${scheme.documents.join('\n* ')}\n\n`;
    }
    response += `**आधिकारिक वेबसाइट:** [${scheme.portal}](${scheme.portal})`;

    return response;
  }

  const handleSend = (text = input) => {
    if (text.trim() === '') return;

    const userMessage = { sender: 'user', text };
    setMessages(prev => [...prev, userMessage]);
    if (input) setInput(''); // Clear input only if it was used
    setIsTyping(true);

    setTimeout(() => {
        const foundScheme = findScheme(text);
        const botResponseText = formatResponse(foundScheme);
        const botMessage = { sender: 'bot', text: botResponseText };
        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);
    }, 1200);
  };
  
  // Use example questions from spec file
  const quickQuestions = chatbotSpec.exampleQuestions.map(q => q.question);

  const handleQuickQuestion = (question) => {
      handleSend(question)
  }

  return (
    <>
      {/* Floating Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 z-50 transition-transform hover:scale-110"
      >
        {isOpen ? <X size={24}/> : <Bot size={24}/>}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-5 w-[90vw] max-w-sm h-[70vh] bg-white rounded-xl shadow-2xl border flex flex-col z-50">
            {/* Header */}
            <div className="p-4 bg-slate-100 rounded-t-xl border-b flex items-center">
                <Bot size={22} className="text-blue-600"/>
                <h3 className="ml-3 font-semibold text-md text-slate-800">{chatbotSpec.chatbotName}</h3>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 overflow-y-auto bg-slate-50">
                {messages.map((msg, index) => (
                    <div key={index} className={`flex items-start gap-2.5 my-3 ${msg.sender === 'user' ? 'justify-end' : ''}`}>
                        {msg.sender === 'bot' && <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0"><Bot className="text-white" size={20}/></div>}
                        <div className={`p-3 rounded-lg max-w-xs text-sm ${msg.sender === 'bot' ? 'bg-slate-200 text-slate-800' : 'bg-blue-600 text-white'}`}>
                           <div dangerouslySetInnerHTML={{ __html: msg.text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br />').replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-700 underline font-semibold">$1</a>') }} />
                        </div>
                    </div>
                ))}
                {isTyping && <div className="flex items-start gap-2.5 my-3"><div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0"><Bot className="text-white" size={20}/></div><div className="p-3 rounded-lg bg-slate-200"><Loader2 className="animate-spin text-slate-500" size={20}/></div></div>}
                <div ref={messagesEndRef} />
            </div>
            
            {/* Quick Questions */}
            <div className="p-2 border-t bg-white">
                <div className="flex flex-wrap gap-2">
                    {quickQuestions.map(q => (
                        <button key={q} onClick={() => handleQuickQuestion(q)} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-full transition-colors">{q}</button>
                    ))}
                </div>
            </div>

            {/* Input */}
            <div className="p-3 bg-slate-100 rounded-b-xl border-t flex items-center">
                <input 
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleSend()}
                    placeholder="योजना के बारे में पूछें..."
                    className="w-full px-4 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <button onClick={() => handleSend()} className="ml-2 flex-shrink-0 bg-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center hover:bg-blue-700 disabled:bg-slate-400 transition-colors" disabled={isTyping || !input.trim()}>
                    <Send size={18}/>
                </button>
            </div>
        </div>
      )}
    </>
  );
}

export default Chatbot;
